import { Box, Switch, TextField, Typography } from '@mui/material';
import {
  SMS_SESSION_DEFAULT_IDLE_SECONDS,
  SMS_SESSION_DEFAULT_NUDGE_ATTEMPTS,
  SMS_SESSION_IDLE_SECONDS_MIN,
  SMS_SESSION_NUDGE_ATTEMPTS_MIN,
} from '@provision/conversation-types';
import { PartnerSettingValue } from '@provision/entity-types';
import { useState } from 'react';
import { SettingsSection, SettingsSectionRow } from 'src/components/SettingsSection';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import {
  PartnerGroupRules,
  PartnerSetting,
  useGroupRules,
  useManagePartnerSettingsValue,
  usePartnerSettings,
} from 'src/graphql';
import { useGlobalStore } from 'src/store';
import { useNotifications } from 'src/utils/hooks';
import { isStringBooleanTrue } from 'src/utils/strings';

type SettingRecord = Partial<PartnerSettingValue>;

// Shown only when the platform rows are absent (DML not applied): the numbers fall back to the
// shared defaults the backend uses too; copy has no built-in text and stays empty.
const NUMERIC_DEFAULTS: Partial<Record<PartnerSetting, string>> = {
  [PartnerSetting.SmsSessionIdleSeconds]: String(SMS_SESSION_DEFAULT_IDLE_SECONDS),
  [PartnerSetting.SmsSessionNudgeAttempts]: String(SMS_SESSION_DEFAULT_NUDGE_ATTEMPTS),
};

const SMS_SETTINGS = [
  PartnerSetting.SmsSessionsOn,
  PartnerSetting.SmsSessionIdleSeconds,
  PartnerSetting.SmsSessionNudgeAttempts,
  PartnerSetting.SmsSessionNudgeCopy,
  PartnerSetting.SmsSessionGoodbyeCopy,
];

function partnerRecord(
  records: SettingRecord[],
  partnerId: number | null,
  settingId: PartnerSetting,
): SettingRecord | undefined {
  return records.find((r) => r.partnerId === partnerId && r.partnerSettingId === settingId);
}

/** Platform row, else the shared numeric default, else empty. */
function platformValue(records: SettingRecord[], settingId: PartnerSetting): string {
  return (
    partnerRecord(records, PLATFORM_PARTNER_ID, settingId)?.value ??
    NUMERIC_DEFAULTS[settingId] ??
    ''
  );
}

/** Partner override wins; the platform value is the fallback. */
function resolveValue(
  records: SettingRecord[],
  partnerId: number | null,
  settingId: PartnerSetting,
): string {
  return (
    partnerRecord(records, partnerId, settingId)?.value ?? platformValue(records, settingId)
  );
}

const copyFieldLabelSx = { mb: 0.5 } as const;

interface IntegerField {
  settingId: PartnerSetting;
  min: number;
  value: string;
  setValue: (value: string) => void;
  setError: (error: string | null) => void;
}

const SmsSessionPreferencesContent = ({ records }: { records: SettingRecord[] }) => {
  const partnerId = useGlobalStore.use.selectedPartnerId();
  const {
    updatePartnerSettingsValue,
    createPartnerSettingsValue,
    deletePartnerSettingsValue,
  } = useManagePartnerSettingsValue(partnerId);
  const { showErrorAlert } = useNotifications();

  const enabled = isStringBooleanTrue(
    resolveValue(records, partnerId, PartnerSetting.SmsSessionsOn),
  );

  // Numeric/text fields are locally controlled and saved on blur. The parent only
  // mounts this after the records load (keyed by partner), so initializers see data.
  const [idleSeconds, setIdleSeconds] = useState(
    resolveValue(records, partnerId, PartnerSetting.SmsSessionIdleSeconds),
  );
  const [nudgeAttempts, setNudgeAttempts] = useState(
    resolveValue(records, partnerId, PartnerSetting.SmsSessionNudgeAttempts),
  );
  const [nudgeCopy, setNudgeCopy] = useState(
    resolveValue(records, partnerId, PartnerSetting.SmsSessionNudgeCopy),
  );
  const [goodbyeCopy, setGoodbyeCopy] = useState(
    resolveValue(records, partnerId, PartnerSetting.SmsSessionGoodbyeCopy),
  );

  const [idleError, setIdleError] = useState<string | null>(null);
  const [nudgeAttemptsError, setNudgeAttemptsError] = useState<string | null>(null);
  const [isSavingEnabled, setIsSavingEnabled] = useState(false);

  async function save(settingId: PartnerSetting, value: string): Promise<void> {
    // Blurring an untouched field must not pin the partner to today's platform default.
    if (value === resolveValue(records, partnerId, settingId)) return;

    const existing = partnerRecord(records, partnerId, settingId);
    if (existing?.partnerSettingValueId) {
      await updatePartnerSettingsValue({
        partnerSettingValueId: existing.partnerSettingValueId,
        value,
        partnerSettingId: settingId,
      });
    } else {
      await createPartnerSettingsValue({ value, partnerSettingId: settingId });
    }
  }

  const handleEnabledToggle = async (checked: boolean): Promise<void> => {
    setIsSavingEnabled(true);
    try {
      await save(PartnerSetting.SmsSessionsOn, String(checked));
    } finally {
      setIsSavingEnabled(false);
    }
  };

  // Clearing any field reverts to the platform default: the partner override row is
  // deleted (never updated to '') and the field resets to the platform value.
  const revertToPlatformDefault = async (
    settingId: PartnerSetting,
    reset: (value: string) => void,
  ): Promise<void> => {
    // At platform level the row IS the shared default every partner falls back to — never delete it.
    if (partnerId === PLATFORM_PARTNER_ID) {
      showErrorAlert('Platform defaults cannot be cleared. Enter a value instead.');
      reset(platformValue(records, settingId));
      return;
    }

    const existing = partnerRecord(records, partnerId, settingId);
    if (existing?.partnerSettingValueId) {
      await deletePartnerSettingsValue(existing.partnerSettingValueId);
    }
    reset(platformValue(records, settingId));
  };

  const handleIntegerBlur = async (field: IntegerField): Promise<void> => {
    const { settingId, min, value, setValue, setError } = field;
    if (!value.trim()) {
      setError(null);
      await revertToPlatformDefault(settingId, setValue);
      return;
    }
    // Same rule as the backend write guard: a whole number at or above the minimum, never truncated.
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed < min) {
      setError(`Must be a whole number of ${min} or more`);
      return;
    }
    setError(null);
    await save(settingId, String(parsed));
  };

  const handleCopyBlur = async (
    settingId: PartnerSetting,
    value: string,
    reset: (value: string) => void,
  ): Promise<void> => {
    if (value.trim()) {
      await save(settingId, value);
      return;
    }
    await revertToPlatformDefault(settingId, reset);
  };

  return (
    <SettingsSection
      title="SMS sessions"
      description="Configure live SMS sessions inside conversations."
    >
      <SettingsSectionRow
        title="Enable SMS sessions"
        secondary="Allow agents to start live SMS sessions from conversation threads."
        control={
          <Switch
            checked={enabled}
            disabled={isSavingEnabled}
            onChange={(e) => void handleEnabledToggle(e.target.checked)}
            inputProps={{ 'aria-label': 'Enable SMS sessions' }}
          />
        }
      />
      <SettingsSectionRow
        title="Idle timeout (seconds)"
        secondary="Seconds of client silence before an idle nudge is sent. The scheduler fires on minute boundaries, so a value is rounded up to the next whole minute."
        control={
          <TextField
            size="small"
            type="number"
            value={idleSeconds}
            onChange={(e) => setIdleSeconds(e.target.value)}
            onBlur={() =>
              void handleIntegerBlur({
                settingId: PartnerSetting.SmsSessionIdleSeconds,
                min: SMS_SESSION_IDLE_SECONDS_MIN,
                value: idleSeconds,
                setValue: setIdleSeconds,
                setError: setIdleError,
              })
            }
            error={!!idleError}
            helperText={idleError}
            inputProps={{
              'aria-label': 'Idle timeout (seconds)',
              min: SMS_SESSION_IDLE_SECONDS_MIN,
              step: 1,
            }}
            sx={{ width: 140 }}
          />
        }
      />
      <SettingsSectionRow
        title="Nudge attempts before ending"
        secondary="Number of nudge messages to send before closing an idle session. Zero closes the session with the end message right away."
        control={
          <TextField
            size="small"
            type="number"
            value={nudgeAttempts}
            onChange={(e) => setNudgeAttempts(e.target.value)}
            onBlur={() =>
              void handleIntegerBlur({
                settingId: PartnerSetting.SmsSessionNudgeAttempts,
                min: SMS_SESSION_NUDGE_ATTEMPTS_MIN,
                value: nudgeAttempts,
                setValue: setNudgeAttempts,
                setError: setNudgeAttemptsError,
              })
            }
            error={!!nudgeAttemptsError}
            helperText={nudgeAttemptsError}
            inputProps={{
              'aria-label': 'Nudge attempts before ending',
              min: SMS_SESSION_NUDGE_ATTEMPTS_MIN,
              step: 1,
            }}
            sx={{ width: 140 }}
          />
        }
      />
      <Box sx={{ px: 2.25, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Typography
            variant="body2"
            fontWeight={600}
            color="custom.OV.sys.onSurface"
            sx={copyFieldLabelSx}
          >
            Idle nudge message
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            value={nudgeCopy}
            onChange={(e) => setNudgeCopy(e.target.value)}
            onBlur={() =>
              void handleCopyBlur(PartnerSetting.SmsSessionNudgeCopy, nudgeCopy, setNudgeCopy)
            }
            inputProps={{ 'aria-label': 'Idle nudge message' }}
          />
        </Box>
        <Box>
          <Typography
            variant="body2"
            fontWeight={600}
            color="custom.OV.sys.onSurface"
            sx={copyFieldLabelSx}
          >
            Session-end message
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            value={goodbyeCopy}
            onChange={(e) => setGoodbyeCopy(e.target.value)}
            onBlur={() =>
              void handleCopyBlur(
                PartnerSetting.SmsSessionGoodbyeCopy,
                goodbyeCopy,
                setGoodbyeCopy,
              )
            }
            inputProps={{ 'aria-label': 'Session-end message' }}
          />
        </Box>
        <Typography variant="caption" color="custom.OV.sys.onSurfaceVariant">
          Changes apply to sessions started after saving.
        </Typography>
      </Box>
    </SettingsSection>
  );
};

/**
 * Settings → Preferences section for the SMS sessions channel. Visible only for
 * tenants granted the sms_sessions_available rule. Timer/copy overrides apply to
 * sessions opened after the change — open sessions keep their frozen-at-open policy.
 */
export const SmsSessionPreferences = () => {
  const { checkValidity } = useGroupRules();
  const partnerId = useGlobalStore.use.selectedPartnerId();
  const { data, loading } = usePartnerSettings({ partnerId, partnerSetting: SMS_SETTINGS });

  if (!checkValidity([PartnerGroupRules.SmsSessionsAvailable]) || loading) {
    return null;
  }

  // Remount on partner switch so the field state re-initializes from that partner's records.
  return <SmsSessionPreferencesContent key={partnerId} records={data} />;
};
